const API_BASE_URL = process.env.REACT_APP_API_BASE || "http://localhost:8080";

// Helper to get auth token
// Handles both Clerk (getToken()) and Firebase (getIdToken is already a promise-returning function)
const getAuthToken = async (getToken, forceRefresh = true) => {
  if (!getToken) return null;
  try {
    // ✅ FIX: Force token refresh by default to get updated email_verified claim
    // Without this, cached tokens still show email_verified: false even after verification
    // Firebase pattern: getToken is the actual getIdToken function from AuthContext
    const token = await getToken(forceRefresh);
    return token;
  } catch (error) {
    return null;
  }
};

/**
 * Create a new comment on a video or reply to a comment
 * @param {string} videoId - Video ID
 * @param {string} content - Comment content
 * @param {function} getToken - Clerk getToken function
 * @param {string} parentId - Optional parent comment ID for replies
 * @returns {Promise<{success: boolean, data?: object, error?: object}>}
 */
export const createComment = async (videoId, content, getToken, parentId = null) => {
  try {
    const token = await getAuthToken(getToken);
    if (!token) {
      return {
        success: false,
        error: { code: "NO_AUTH", message: "Authentication required" },
      };
    }

    const body = { videoId, content };
    if (parentId) {
      body.parentId = parentId;
    }

    const response = await fetch(`${API_BASE_URL}/api/comments`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    const data = await response.json();
    return data;
  } catch (error) {
    return {
      success: false,
      error: { code: "NETWORK_ERROR", message: error.message },
    };
  }
};

/**
 * Get top-level comments for a video
 * @param {string} videoId - Video ID
 * @param {function} getToken - Optional Clerk getToken function for like states
 * @returns {Promise<{success: boolean, data?: array, error?: object}>}
 */
export const getComments = async (videoId, getToken = null) => {
  try {
    const headers = {};

    // Include auth token if available (for like states)
    if (getToken) {
      const token = await getAuthToken(getToken);
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    } else {
    }

    const response = await fetch(
      `${API_BASE_URL}/api/comments?videoId=${videoId}`,
      { headers }
    );

    const data = await response.json();
    return data;
  } catch (error) {
    return {
      success: false,
      error: { code: "NETWORK_ERROR", message: error.message },
    };
  }
};

/**
 * Get replies for a specific comment
 * @param {string} commentId - Comment ID
 * @param {function} getToken - Optional Clerk getToken function for like states
 * @returns {Promise<{success: boolean, data?: array, error?: object}>}
 */
export const getCommentReplies = async (commentId, getToken = null) => {
  try {
    const headers = {};

    // Include auth token if available (for like states)
    if (getToken) {
      const token = await getAuthToken(getToken);
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }

    const response = await fetch(
      `${API_BASE_URL}/api/comments/${commentId}/replies`,
      { headers }
    );

    const data = await response.json();
    return data;
  } catch (error) {
    return {
      success: false,
      error: { code: "NETWORK_ERROR", message: error.message },
    };
  }
};
